import React, {
  ComponentClass,
  useState,
  MouseEventHandler,
  useEffect,
  forwardRef,
} from "react";
import Select, {
  components,
  MultiValueGenericProps,
  MultiValueProps,
  OnChangeValue,
  Props,
} from "react-select";
import {
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortEndHandler,
  SortableHandle,
} from "react-sortable-hoc";
import useTheme from "@mui/styles/useTheme";
import { FormControl, InputLabel } from "@mui/material";

import { IResponseMany, IQueryMany } from "../../interfaces";

function arrayMove<T>(array: readonly T[], from: number, to: number) {
  const slicedArray = array.slice();
  slicedArray.splice(
    to < 0 ? array.length + to : to,
    0,
    slicedArray.splice(from, 1)[0]
  );
  return slicedArray;
}

// See example at https://react-select.com/advanced#sortable-multiselect

const SortableMultiValue = SortableElement((props: MultiValueProps<any>) => {
  // this prevents the menu from being opened/closed when the user clicks
  // on a value to begin dragging it. ideally, detecting a click (instead of
  // a drag) would still focus the control and toggle the menu, but that
  // requires some magic with refs that are out of scope for this example
  const onMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const innerProps = { ...props.innerProps, onMouseDown };
  return <components.MultiValue {...props} innerProps={innerProps} />;
});

const SortableMultiValueLabel = SortableHandle(
  (props: MultiValueGenericProps) => <components.MultiValueLabel {...props} />
);

const SortableSelect = SortableContainer(Select) as ComponentClass<
  Props<any, true> & SortableContainerProps
>;

interface SortableSelectProps {
  value?: any[];
  onChange?: (selectedOptions: OnChangeValue<any, true>) => void;
  className?: string;
  label?: string;
  placeholder?: string;
  getOptionLabel: (option: any) => string;
  getOptionValue: (option: any) => string;
  fetcher: (query: IQueryMany) => IResponseMany;
  query: (search: string) => IQueryMany;
}

const SortableSelectComponent = forwardRef(
  (
    {
      fetcher,
      query,
      value = [],
      className,
      label,
      placeholder,
      getOptionLabel,
      getOptionValue,
      onChange,
    }: SortableSelectProps,
    ref
  ) => {
    const theme = useTheme();

    // function to convert provided options to options that are a valid SortableSelect prop
    const toSelectOptions = (options) => {
      return (
        options?.map((option, index) => ({
          ...option,
          label: getOptionLabel(option),
          value: getOptionValue(option),
          index: index,
        })) || []
      );
    };

    const [searchInput, setSearchInput] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const { data, awaitMore, nextPage, isValidating } = fetcher(
      query(searchInput)
    );
    const [options, setOptions] = useState(toSelectOptions(data));
    const [selected, setSelected] = useState<readonly any[]>(
      toSelectOptions(value)
    );

    const handleMenuScrollToBottom = () => {
      if (awaitMore) {
        nextPage();
      }
    };

    useEffect(() => {
      const mappedOptions = toSelectOptions(data);
      if (JSON.stringify(mappedOptions) !== JSON.stringify(options)) {
        setOptions(mappedOptions);
      }
    }, [data, getOptionLabel, getOptionValue]);

    const handleChange = (selectedOptions: OnChangeValue<any, true>) => {
      setSelected(selectedOptions);
      if (onChange) {
        onChange(selectedOptions);
      }
    };

    const onSortEnd: SortEndHandler = ({ oldIndex, newIndex }) => {
      const newValue = arrayMove(selected, oldIndex, newIndex);
      setSelected(newValue);
      if (onChange) {
        onChange(newValue);
      }
    };

    return (
      <FormControl fullWidth>
        {label && (
          <InputLabel
            shrink={!!value?.length || !!searchInput || isFocused}
            sx={{
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(1),
              "&.Mui-focused": {
                color: theme.palette.primary.main,
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <SortableSelect
          className={`marapp-qa-select-${className}`}
          useDragHandle
          helperClass="sortableHelper"
          axis="xy"
          onSortEnd={onSortEnd}
          distance={4}
          getHelperDimensions={({ node }) => node.getBoundingClientRect()}
          isMulti
          options={options}
          value={selected}
          onChange={handleChange}
          innerRef={ref}
          components={{
            MultiValue: SortableMultiValue,
            MultiValueLabel: SortableMultiValueLabel,
          }}
          closeMenuOnSelect={false}
          placeholder={placeholder}
          onMenuScrollToBottom={handleMenuScrollToBottom}
          onInputChange={(inputValue) => {
            setSearchInput(inputValue);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          filterOption={null}
          isLoading={isValidating}
          styles={{
            control: (provided, state) => ({
              ...provided,
              backgroundColor: theme.palette.background.paper,
              borderColor: "transparent",
              borderWidth: "1px",
              borderStyle: "solid",
              borderRadius: theme.shape.borderRadius,
              boxShadow: state.isFocused
                ? `0 0 0 1px ${theme.palette.primary.main}`
                : "none",
              minHeight: "56px", // Match MUI TextField height
              "&:hover": {
                borderColor: "transparent",
              },
            }),
            menu: (provided) => ({
              ...provided,
              zIndex: theme.zIndex.modal + 10, // Higher than modal z-index
            }),
            menuList: (provided) => ({
              ...provided,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              padding: theme.spacing(1, 0),
              maxHeight: "40vh",
            }),
            option: (styles, { isFocused, isSelected }) => ({
              ...styles,
              color: theme.palette.text.primary,
              backgroundColor: isSelected
                ? theme.palette.action.selected
                : isFocused
                ? theme.palette.action.hover
                : "transparent",
              padding: theme.spacing(1, 2),
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }),
            multiValue: (styles) => ({
              ...styles,
              backgroundColor: theme.palette.text.primary,
              borderRadius: "16px",
              margin: theme.spacing(0.25),
              zIndex: 1250,
              overflow: "hidden",
              cursor: "move",
            }),
            multiValueRemove: (styles) => ({
              ...styles,
              cursor: "pointer",
              color: theme.palette.grey[900],
              transition: theme.transitions.create("background", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              "&:hover": {
                background: theme.palette.grey[300],
                color: theme.palette.grey[900],
              },
            }),
            multiValueLabel: (styles) => ({
              ...styles,
              color: theme.palette.grey[900],
              padding: theme.spacing(0.5, 1.5),
              paddingLeft: theme.spacing(1.5),
            }),
            input: (styles) => ({
              ...styles,
              color: theme.palette.text.primary,
              margin: theme.spacing(0.5),
            }),
            placeholder: (styles) => ({
              ...styles,
              color: theme.palette.text.secondary,
              display: "none",
            }),
            singleValue: (styles) => ({
              ...styles,
              color: theme.palette.text.primary,
            }),
            indicatorSeparator: (styles) => ({
              ...styles,
              backgroundColor: theme.palette.divider,
            }),
            dropdownIndicator: (styles) => ({
              ...styles,
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
              },
            }),
            clearIndicator: (styles) => ({
              ...styles,
              color: theme.palette.text.secondary,
              "&:hover": {
                color: theme.palette.text.primary,
              },
            }),
          }}
        />
      </FormControl>
    );
  }
);

export default SortableSelectComponent;
